// ══════════════════════════════════════════════════════════════════════════════
// PAGE MAILS PRO - Boîte Gmail connectée
// AbracadaWorld - Cockpit Pro (Artisans/Vendeurs)
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, Inbox, Send, Star, Trash2, Archive, RefreshCw, Search,
  ChevronLeft, ChevronRight, X, Reply, Forward, Paperclip,
  Clock, User, AlertCircle, Check, LogOut, Plus, Edit3, ArrowLeft, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getToken = () => {
  return localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
};

export default function MailsProPage() {
  const [googleStatus, setGoogleStatus] = useState({ connected: false, loading: true });
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile] = useState(window.innerWidth < 768);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  
  const [newEmail, setNewEmail] = useState({
    to: '',
    subject: '',
    body: ''
  });

  // Détection du clavier et masquage navbar
  useEffect(() => {
    if (!showComposeModal || !isMobile) {
      setKeyboardHeight(0);
      return;
    }
    
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [showComposeModal, isMobile]);

  // Vérifier la connexion Google
  const checkGoogleStatus = async () => {
    try {
      const token = getToken();
      if (!token) {
        setGoogleStatus({ connected: false, loading: false });
        return;
      }
      const res = await fetch(`${API_URL}/api/google/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGoogleStatus({ ...data, loading: false });
        if (data.connected) {
          loadEmails();
        }
      } else {
        setGoogleStatus({ connected: false, loading: false });
      }
    } catch (error) {
      setGoogleStatus({ connected: false, loading: false });
    }
  };

  // Connecter Google
  const connectGoogle = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/google/auth/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const { auth_url } = await res.json();
        window.location.href = auth_url;
      } else {
        toast.error('Erreur de connexion Google');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Déconnecter Google
  const disconnectGoogle = async () => {
    if (!window.confirm('Déconnecter ton compte Google ?')) return;
    
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/google/disconnect`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleStatus({ connected: false, loading: false });
      setEmails([]);
      toast.success('Compte Google déconnecté');
    } catch (error) {
      toast.error('Erreur de déconnexion');
    }
  };

  // Charger les emails
  const loadEmails = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/google/emails?max_results=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setEmails(data.emails || []);
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  // Envoyer un email
  const sendEmail = async () => {
    if (!newEmail.to || !newEmail.subject) {
      toast.error('Le destinataire et le sujet sont obligatoires');
      return;
    }

    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/google/emails/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newEmail)
      });

      if (res.ok) {
        toast.success('Email envoyé !');
        setShowComposeModal(false);
        setNewEmail({ to: '', subject: '', body: '' });
        loadEmails();
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Répondre à un email
  const replyToEmail = (email) => {
    const fromMatch = email.from.match(/<(.+)>/);
    const replyTo = fromMatch ? fromMatch[1] : email.from;
    
    setNewEmail({
      to: replyTo,
      subject: email.subject.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
      body: `\n\n--- Message original ---\nDe: ${email.from}\n\n${email.snippet}...`
    });
    setShowComposeModal(true);
    setSelectedEmail(null);
  };

  useEffect(() => {
    checkGoogleStatus();
    
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'connected') {
      toast.success('Compte Google connecté !');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) {
      return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    }
  };

  const extractName = (fromString) => {
    const match = fromString.match(/^([^<]+)/);
    return match ? match[1].trim().replace(/"/g, '') : fromString;
  };

  const filteredEmails = emails.filter(email => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      email.subject?.toLowerCase().includes(query) ||
      email.from?.toLowerCase().includes(query) ||
      email.snippet?.toLowerCase().includes(query)
    );
  });

  // État non connecté
  if (!googleStatus.loading && !googleStatus.connected) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-lg p-8 text-center">
          {/* Badge Beta */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold mb-4">
            <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
            Version Beta
          </div>
          
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#1a2a4a] to-[#2d4a6a] flex items-center justify-center">
            <Mail size={36} className="text-white" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Boîte Mail Pro
          </h1>
          <p className="text-gray-500 mb-6">
            Connecte ton compte pour gérer tes emails professionnels directement depuis ton cockpit.
          </p>
          
          {/* Gmail - Disponible */}
          <button
            onClick={connectGoogle}
            data-testid="connect-gmail-pro-btn"
            className="w-full py-4 bg-white border-2 border-gray-200 rounded-2xl flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors mb-3"
          >
            <img src="https://www.google.com/favicon.ico" alt="Google" className="w-6 h-6" />
            <span className="font-semibold text-gray-700">Gmail</span>
            <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Disponible</span>
          </button>
          
          {/* Microsoft Outlook - Prochainement */}
          <div className="w-full py-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center gap-3 mb-3 opacity-60">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7v10l10 5 10-5V7L12 2z" fill="#0078D4"/>
              <path d="M12 2v20M2 7l10 5 10-5" stroke="#fff" strokeWidth="1"/>
            </svg>
            <span className="font-semibold text-gray-500">Microsoft Outlook</span>
            <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">Prochainement</span>
          </div>
          
          {/* iCloud Mail - Prochainement */}
          <div className="w-full py-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center gap-3 opacity-60">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <path d="M19.5 10.5c0-4.14-3.36-7.5-7.5-7.5S4.5 6.36 4.5 10.5c0 .17.01.34.02.5A5.5 5.5 0 006 21.5h12a5.5 5.5 0 001.5-10.8c0-.07 0-.13 0-.2z" fill="#007AFF"/>
            </svg>
            <span className="font-semibold text-gray-500">iCloud Mail</span>
            <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">Prochainement</span>
          </div>
          
          <p className="mt-6 text-xs text-gray-400">
            Tes données restent privées et sécurisées.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Pro - Bleu marine */}
      <div 
        className="sticky top-0 z-20 px-4 py-4"
        style={{ 
          background: 'linear-gradient(135deg, #1a2a4a 0%, #2d4a6a 100%)',
          paddingTop: 'max(16px, env(safe-area-inset-top))'
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-white">Ma Boîte Mail</h1>
            {googleStatus.email && (
              <p className="text-white/60 text-sm">{googleStatus.email}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowComposeModal(true)}
              className="w-10 h-10 rounded-full bg-[#E8650A] flex items-center justify-center shadow-lg"
            >
              <Edit3 size={20} className="text-white" />
            </button>
            <button
              onClick={loadEmails}
              disabled={loading}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
            >
              <RefreshCw size={18} className={`text-white ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={disconnectGoogle}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
            >
              <LogOut size={18} className="text-white/70" />
            </button>
          </div>
        </div>

        {/* Recherche */}
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher..."
            className="w-full pl-12 pr-4 py-3 bg-white rounded-xl"
          />
        </div>
      </div>

      {/* Liste des emails */}
      <div className="pb-32">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#1a2a4a] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredEmails.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Mail size={48} className="mx-auto text-gray-300 mb-3" />
            <p>Aucun email</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredEmails.map((email) => (
              <motion.div
                key={email.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={() => setSelectedEmail(email)}
                className={`px-4 py-4 bg-white cursor-pointer hover:bg-gray-50 ${
                  email.is_unread ? 'border-l-4 border-[#E8650A]' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1a2a4a] to-[#2d4a6a] flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-white">
                      {extractName(email.from).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm truncate ${email.is_unread ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
                        {extractName(email.from)}
                      </span>
                      <span className="text-xs text-gray-400 ml-2">
                        {formatDate(email.date)}
                      </span>
                    </div>
                    <p className={`text-sm truncate ${email.is_unread ? 'font-semibold' : ''}`}>
                      {email.subject}
                    </p>
                    <p className="text-xs text-gray-400 truncate mt-0.5">
                      {email.snippet}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal détail email */}
      <AnimatePresence>
        {selectedEmail && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setSelectedEmail(null)}
            />
            <motion.div
              initial={{ opacity: 0, y: '100%' }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: '100%' }}
              className="fixed inset-x-0 bottom-0 top-20 bg-white rounded-t-3xl z-50 flex flex-col"
            >
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-start justify-between mb-3">
                  <button 
                    onClick={() => setSelectedEmail(null)}
                    className="p-2 -ml-2 hover:bg-gray-100 rounded-full"
                  >
                    <ChevronLeft size={24} className="text-gray-600" />
                  </button>
                  <button 
                    onClick={() => replyToEmail(selectedEmail)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <Reply size={20} className="text-gray-600" />
                  </button>
                </div>
                
                <h2 className="text-lg font-bold text-gray-800 mb-2">
                  {selectedEmail.subject}
                </h2>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1a2a4a] to-[#2d4a6a] flex items-center justify-center">
                    <span className="text-sm font-bold text-white">
                      {extractName(selectedEmail.from).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{extractName(selectedEmail.from)}</p>
                    <p className="text-xs text-gray-500">{selectedEmail.date}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {selectedEmail.snippet}...
                </p>
              </div>
              
              <div className="p-4 border-t border-gray-100">
                <button
                  onClick={() => replyToEmail(selectedEmail)}
                  className="w-full py-3.5 bg-[#1a2a4a] text-white rounded-2xl font-semibold flex items-center justify-center gap-2"
                >
                  <Reply size={20} />
                  Répondre
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Modal composer - Version mobile plein écran / Desktop centré */}
      <AnimatePresence>
        {showComposeModal && (
          isMobile ? (
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="fixed inset-0 z-50 flex flex-col bg-white"
            >
              {/* Header */}
              <header className="flex items-center gap-2 px-3 py-3 border-b border-gray-100">
                <button onClick={() => setShowComposeModal(false)} className="p-1 text-gray-500">
                  <ArrowLeft size={22} />
                </button>
                <h1 className="text-[15px] font-bold text-gray-900">Nouveau message</h1>
              </header>

              {/* Contenu scrollable */}
              <div className="flex-1 overflow-y-auto p-5">
                <div className="space-y-4">
                  <div>
                    <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Destinataire *</label>
                    <input
                      type="email"
                      value={newEmail.to}
                      onChange={(e) => setNewEmail({ ...newEmail, to: e.target.value })}
                      placeholder="destinataire@email.com"
                      className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                    />
                  </div>

                  <div>
                    <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Sujet *</label>
                    <input
                      type="text"
                      value={newEmail.subject}
                      onChange={(e) => setNewEmail({ ...newEmail, subject: e.target.value })}
                      placeholder="Objet..."
                      className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                    />
                  </div>

                  <div>
                    <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Message</label>
                    <textarea
                      value={newEmail.body}
                      onChange={(e) => setNewEmail({ ...newEmail, body: e.target.value })}
                      placeholder="Ton message..."
                      rows={6}
                      className="w-full p-4 rounded-xl border text-[15px] resize-none bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                    />
                  </div>
                </div>
              </div>

              {/* Footer fixe */}
              <div 
                className="p-4 border-t border-gray-100 bg-white transition-all duration-200"
                style={{ 
                  paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
                  marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
                }}
              >
                <button
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
                >
                  <Sparkles size={16} />
                  Créer avec Abracada
                </button>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowComposeModal(false)}
                    className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={sendEmail}
                    disabled={!newEmail.to || !newEmail.subject}
                    className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Send size={18} />
                    Envoyer
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-50"
                onClick={() => setShowComposeModal(false)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
              >
                <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-bold text-gray-800">Nouveau message</h2>
                      <button onClick={() => setShowComposeModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                        <X size={20} />
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">À</label>
                        <input
                          type="email"
                          value={newEmail.to}
                          onChange={(e) => setNewEmail({ ...newEmail, to: e.target.value })}
                          placeholder="destinataire@email.com"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1a2a4a] focus:ring-1 focus:ring-[#1a2a4a]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sujet</label>
                        <input
                          type="text"
                          value={newEmail.subject}
                          onChange={(e) => setNewEmail({ ...newEmail, subject: e.target.value })}
                          placeholder="Objet..."
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1a2a4a] focus:ring-1 focus:ring-[#1a2a4a]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                        <textarea
                          value={newEmail.body}
                          onChange={(e) => setNewEmail({ ...newEmail, body: e.target.value })}
                          placeholder="Ton message..."
                          rows={8}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1a2a4a] focus:ring-1 focus:ring-[#1a2a4a] resize-none"
                        />
                      </div>

                      <div className="flex gap-3 pt-4">
                        <button
                          onClick={() => setShowComposeModal(false)}
                          className="flex-1 py-3 border border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          Annuler
                        </button>
                        <button
                          className="px-4 py-3 rounded-xl font-semibold text-white flex items-center gap-2"
                          style={{ background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)' }}
                        >
                          <Sparkles size={16} />
                          Demander à Abra
                        </button>
                        <button
                          onClick={sendEmail}
                          className="flex-1 py-3 bg-[#E8650A] text-white rounded-xl font-semibold hover:bg-orange-600 flex items-center justify-center gap-2"
                        >
                          <Send size={20} />
                          Envoyer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )
        )}
      </AnimatePresence>
    </div>
  );
}
